import { Button, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import React, { useContext } from 'react';

import { AppContext } from '~/AppContext';
import { ApplicationAndRole } from '../CreateUserApplicationRole/CreateUserApplicationRole';
import { BEM } from '@feedfm/feed-util';
import { hasPermission } from '~/components/RestrictedAccess/RestrictedAccess';

export interface ApplicationAndRoleWithPermissions extends ApplicationAndRole {
  permissions: string[];
}

export interface UserApplicationRolesProps {
  applicationRoles: ApplicationAndRoleWithPermissions[];
  onDelete?: ( applicationRole: ApplicationAndRole ) => void;
  className?: string;
}

const bem = new BEM( 'user-application-roles' );

export const UserApplicationRoles: React.FC<UserApplicationRolesProps> = ( {
  applicationRoles,
  onDelete,
  className,
} ) => {
  const context = useContext( AppContext );

  const handleDelete = ( applicationRole: ApplicationAndRole ) => {
    if ( onDelete ) {
      onDelete( applicationRole );
    }
  };

  if ( applicationRoles.length === 0 ) {
    return null;
  }

  const canDelete = hasPermission( ['user:grant'], context );

  const renderPermissionsTooltip = ( applicationRole: ApplicationAndRoleWithPermissions, index: number ) => {
    const permissions = applicationRole.permissions || [];

    return (
      <Tooltip id={ `application-role-permissions-${applicationRole.application.id}-${index}` }>
        { permissions.length > 0 ? (
          <ul className="mb-0 list-unstyled">
            { permissions
              .slice()
              .sort()
              .map( ( permission: string ) => (
                <li key={ permission }>{ permission }</li>
              ) ) }
          </ul>
        ) : (
          <span>No permissions listed</span>
        ) }
      </Tooltip>
    );
  };

  return (
    <Table hover className={ BEM.$( bem, className ) } size="sm">
      <thead>
        <tr>
          <th className="text-left">Application</th>
          <th className="text-left">Role</th>
          { canDelete && <th></th> }
        </tr>
      </thead>
      <tbody>
        { [...applicationRoles]
          .sort( ( a, b ) => a.application.name.localeCompare( b.application.name ) )
          .map( ( applicationRole, index ) => (
            <tr key={ `${applicationRole.application.id}-${applicationRole.role}-${index}` }>
              <td>
                { applicationRole.application.name } <span className="details">{ applicationRole.application.id }</span>
              </td>
              <td>
                <OverlayTrigger placement="top" overlay={ renderPermissionsTooltip( applicationRole, index ) }>
                  <span>{ applicationRole.role }</span>
                </OverlayTrigger>
              </td>
              { canDelete && (
                <td>
                  <Button variant="outline-danger" size="sm" onClick={ () => handleDelete( applicationRole ) }>
                    Delete
                  </Button>
                </td>
              ) }
            </tr>
          ) ) }
      </tbody>
    </Table>
  );
};

UserApplicationRoles.displayName = 'UserApplicationRoles';
